/**
 * The documents of the legajo, and how to add more.
 *
 * The type of each file is the system's problem, not the analyst's: it classifies them, and the
 * override is there for when it gets one wrong, out of the way of the common case.
 */

import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, friendlyError } from "../../api/client";
import { DOC_TYPES, DOC_TYPE_SOURCE, label } from "../../lib/labels";
import type { CaseOut } from "../../api/types";
import { Button, EmptyState, ErrorBanner, SectionTitle, Table, Td, Th } from "../../components/ui";

const ACCEPT = ".pdf,.docx,.xlsx,.png,.jpg,.jpeg";

export default function Documents({ kase }: { kase: CaseOut }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [docType, setDocType] = useState<string>("");
  const [dragging, setDragging] = useState(false);

  const upload = useMutation({
    mutationFn: () => api.uploadDocuments(kase.id, files, docType || null),
    onSuccess: async () => {
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
      await queryClient.invalidateQueries({ queryKey: ["case", kase.id] });
      await queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });

  return (
    <>
      {kase.documents.length ? (
        <>
          <SectionTitle>Documentos del legajo · {kase.documents.length}</SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>Archivo</Th>
                <Th>Tipo</Th>
                <Th right>Páginas</Th>
                <Th>Clasificación</Th>
              </tr>
            </thead>
            <tbody>
              {kase.documents.map((document) => (
                <tr key={document.id}>
                  <Td className="font-medium">
                    <a
                      href={api.documentUrl(document.id)}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
                    >
                      {document.filename}
                    </a>
                  </Td>
                  <Td>{label(DOC_TYPES, document.doc_type)}</Td>
                  <Td right className="text-muted">
                    {document.pages ?? "—"}
                  </Td>
                  <Td className="text-muted">{label(DOC_TYPE_SOURCE, document.doc_type_source)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      ) : (
        <EmptyState
          title="Este legajo no tiene documentos"
          text="Subí los estados contables, el informe de bureau y el memo para poder analizarlo."
        />
      )}

      <SectionTitle>Agregar documentos</SectionTitle>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          setFiles([...e.dataTransfer.files]);
        }}
        className={`rounded-xl border border-dashed px-6 py-10 text-center ${
          dragging ? "border-primary bg-canvas" : "border-line"
        }`}
      >
        <p className="text-sm">Arrastrá los archivos acá</p>
        <p className="mt-1 text-xs text-muted">
          PDF, Word, Excel o imágenes. El sistema identifica solo de qué tipo es cada uno.
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={(e) => setFiles([...(e.target.files ?? [])])}
          className="sr-only"
          id="file-input"
        />
        <Button className="mt-4" onClick={() => inputRef.current?.click()}>
          Buscar archivos
        </Button>
      </div>

      {files.length ? (
        <div className="mt-4 max-w-2xl">
          <ul className="space-y-1 text-sm">
            {files.map((file) => (
              <li key={file.name} className="flex justify-between gap-4">
                <span className="truncate">{file.name}</span>
                <span className="shrink-0 text-muted">{Math.ceil(file.size / 1024)} KB</span>
              </li>
            ))}
          </ul>

          <details className="mt-4">
            <summary className="cursor-pointer text-sm text-muted">
              El sistema clasificó mal un documento
            </summary>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="mt-2 rounded-lg border border-line px-3 py-2 text-sm"
            >
              <option value="">Detectar automáticamente</option>
              {Object.entries(DOC_TYPES).map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </select>
          </details>

          <Button
            variant="primary"
            className="mt-4"
            disabled={upload.isPending}
            onClick={() => upload.mutate()}
          >
            {upload.isPending ? "Subiendo…" : `Subir ${files.length} documento(s)`}
          </Button>
        </div>
      ) : null}

      {upload.isError ? (
        <div className="mt-4 max-w-2xl">
          <ErrorBanner {...friendlyError(upload.error)} />
        </div>
      ) : null}
    </>
  );
}
